import { Trash2, Edit2, Check, AlertCircle } from 'lucide-react';
import { colors } from '../../styles/colors';
import { formatEuro } from '../../utils/formatters';
import { DatePicker } from '../ui/DatePicker';

/**
 * PaymentTable - Tableau des paiements avec édition inline
 *
 * @param {Object} props
 * @param {Array} props.payments - Liste des paiements
 * @param {boolean} props.isFacturePayee - Si la facture est entièrement payée
 * @param {boolean} props.isFacturePayeeSansHistorique - Si payée sans historique
 * @param {string|null} props.editingPaymentId - ID du paiement en cours d'édition
 * @param {Object} props.formData - Données du formulaire d'édition
 * @param {Function} props.onFormChange - Handler changement formulaire
 * @param {Function} props.onEdit - Handler édition
 * @param {Function} props.onSave - Handler sauvegarde
 * @param {Function} props.onDelete - Handler suppression
 * @param {boolean} props.saving - État de sauvegarde
 */
export default function PaymentTable({
  payments,
  isFacturePayee,
  isFacturePayeeSansHistorique,
  editingPaymentId,
  formData,
  onFormChange,
  onEdit,
  onSave,
  onDelete,
  saving
}) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR');
  };

  const thStyle = {
    padding: '0.75rem',
    textAlign: 'left',
    color: colors.text.white,
    fontWeight: '600'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.375rem',
    border: `1px solid ${colors.secondary.lightGray}`,
    borderRadius: '0.25rem'
  };

  const actionButtonStyle = {
    padding: '0.375rem',
    color: colors.text.white,
    border: 'none',
    borderRadius: '0.25rem',
    cursor: saving ? 'not-allowed' : 'pointer',
    display: 'flex'
  };

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{
          fontSize: '1.125rem',
          fontWeight: '600',
          color: colors.primary.gray,
          margin: 0
        }}>
          Historique des paiements
        </h3>
        {(isFacturePayee || isFacturePayeeSansHistorique) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#d1fae5',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            color: '#065f46',
            fontWeight: '500'
          }}>
            <Check size={16} />
            Consultation seule
          </div>
        )}
      </div>

      {/* Contenu */}
      {payments.length === 0 ? (
        <EmptyState isFacturePayeeSansHistorique={isFacturePayeeSansHistorique} />
      ) : (
        <div style={{
          border: `1px solid ${colors.secondary.lightGray}`,
          borderRadius: '0.5rem',
          overflow: 'hidden',
          marginBottom: '2rem'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: colors.secondary.lightTeal }}>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Montant</th>
                <th style={thStyle}>Référence</th>
                {!isFacturePayee && (
                  <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr key={payment.id} style={{
                  borderBottom: `1px solid ${colors.secondary.lightGray}`,
                  backgroundColor: index % 2 === 0 ? colors.text.white : '#f9fafb'
                }}>
                  {/* Date */}
                  <td style={{ padding: '0.75rem' }}>
                    {editingPaymentId === payment.id && !isFacturePayee ? (
                      <DatePicker
                        value={formData.date}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={(v) => onFormChange('date', v)}
                        style={inputStyle}
                      />
                    ) : (
                      formatDate(payment.fields.Date)
                    )}
                  </td>

                  {/* Montant */}
                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>
                    {editingPaymentId === payment.id && !isFacturePayee ? (
                      <input
                        type="number"
                        step="0.01"
                        value={formData.montant}
                        onChange={(e) => onFormChange('montant', e.target.value)}
                        style={{ ...inputStyle, textAlign: 'right' }}
                      />
                    ) : (
                      formatEuro(payment.fields.Montant)
                    )}
                  </td>

                  {/* Référence */}
                  <td style={{ padding: '0.75rem' }}>
                    {editingPaymentId === payment.id && !isFacturePayee ? (
                      <input
                        type="text"
                        value={formData.referenceBancaire}
                        onChange={(e) => onFormChange('referenceBancaire', e.target.value)}
                        placeholder="Référence"
                        style={inputStyle}
                      />
                    ) : (
                      payment.fields['Référence bancaire'] || '-'
                    )}
                  </td>

                  {/* Actions */}
                  {!isFacturePayee && (
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        {editingPaymentId === payment.id ? (
                          <button
                            onClick={() => onSave(payment.id)}
                            disabled={saving}
                            style={{ ...actionButtonStyle, backgroundColor: colors.primary.teal }}
                            title="Enregistrer"
                          >
                            <Check size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => onEdit(payment)}
                            disabled={saving}
                            style={{ ...actionButtonStyle, backgroundColor: colors.secondary.lightBlue }}
                            title="Modifier"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => onDelete(payment.id)}
                          disabled={saving}
                          style={{ ...actionButtonStyle, backgroundColor: '#dc2626' }}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function EmptyState({ isFacturePayeeSansHistorique }) {
  return (
    <div style={{
      padding: '2rem',
      textAlign: 'center',
      color: colors.secondary.mediumGray,
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem'
    }}>
      <AlertCircle size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
      <p style={{ margin: 0, fontWeight: '500' }}>
        {isFacturePayeeSansHistorique
          ? "L'historique des paiements n'est pas disponible pour cette facture"
          : "Aucun paiement enregistré"
        }
      </p>
      {isFacturePayeeSansHistorique && (
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: colors.text.secondary }}>
          Cette facture a été marquée comme payée sans détail des paiements
        </p>
      )}
    </div>
  );
}
